import {z} from 'zod'
import {match} from 'ts-pattern'
import {mapKeys, pick, pipe} from 'remeda'
import {comparisonCfgKey, comparisonCfgKeyValue} from './comparison'
import {questionForecastMd, zQuestionCfgShape} from './questionType'

export * from './questionType'
export * from './comparison'

export const numberFormatTable = [
  ['2024.9', '"in the year "0000', 'in the year 2025', 'double quote text at the start or end'],
  [
    '2024.9',
    '"in the year "0000|floor',
    'in the year  2024',
    'end the format with |floor or |ceil to customize rounding'
  ],
  ['1230974', 'a', '1.2m', 'automatically shows a nice amount of digits (2) with an automatic unit (e.g. k, m, t)'],
  ['76000.5', 'a" AUD"', '76k AUD', 'a = formatting with auto-units, double quot to add custom text at the end'],
  ['1230974', '0.0a', '1.2m'],
  ['1460', '0 a', '1 k'],
  ['-104000', '0a', '-104k'],
  ['10000', '0,0.0000', '10,000.0000'],
  ['10000.23', '0,0', '10,000'],
  ['10000.23', '+0,0', '+10,000'],
  ['-10000', '0,0.0', '-10,000.0'],
  ['10000.1234', '0.000', '10000.123'],
  ['100.1234', '00000', '00100'],
  ['1000.1234', '000000,0', '001,000'],
  ['10', '000.00', '010.00'],
  ['10000.1234', '0[.]00000', '10000.12340'],
  ['-10000', '(0,0.0000)', '(10,000.0000)'],
  ['-0.23', '.00', '-.23'],
  ['-0.23', '(.00)', '(.23)'],
  ['0.23', '0.00000', '0.23000'],
  ['0.23', '0.0[0000]', '0.23'],
  ['1', '0o', '1st'],
  ['100', '0o', '100th'],
  ['1000.234', '$0,0.00', '$1,000.23'],
  ['1000.2', '0,0[.]00 $', '1,000.20 $'],
  ['1001', '$ 0,0[.]00', '$ 1,001'],
  ['-1000.234', '($0,0)', '($1,000)'],
  [' -1000.234', '$0.00', '-$1000.23'],
  ['1230974', '($ 0.00 a)', '$ 1.23 m']
]

export const numberFormatTableText = [['Number', 'NumberFormat', 'Output', 'Note'], ...numberFormatTable]
  .map(row => row.map(v => v ?? '').join(' | '))
  .map(row => `| ${row} |`)
  .join('\n')

export const upgradeBoundsVersion = (o: any) =>
  match(o)
    .when(
      o => o && (!('boundsVersion' in o) || o.boundsVersion == '1'),
      o => {
        // if the bounds version is 1, then use the v1 schema to parse it and then convert it to v2
        const zBound = z.object({value: z.number(), hard: z.boolean().optional()})
        const {defaultBounds: defaultBoundsV1} = z
          .object({
            defaultBounds: z.object({min: zBound.default({value: 0}), max: zBound.default({value: 100})}).default({})
          })
          .parse(o)

        const defaultBounds = {
          min: defaultBoundsV1.min.value,
          max: defaultBoundsV1.max.value
        }
        const hardBounds = {
          min: defaultBoundsV1.min.hard ? defaultBoundsV1.min.value : undefined,
          max: defaultBoundsV1.max.hard ? defaultBoundsV1.max.value : undefined
        }
        return {...o, defaultBounds, hardBounds, boundsVersion: '2'}
      }
    )
    .otherwise(() => o)

export const upgradeComparisonKeys = (o: any) =>
  match(o)
    .when(
      o => o && 'comparisons' in o && o.comparisons != null,
      () => {
        const comparisons = mapKeys(o.comparisons, k => comparisonCfgKey(comparisonCfgKeyValue(k)))
        return {...o, comparisons}
      }
    )
    .otherwise(() => o)

export const zQuestionCfg = z.preprocess(
  o => (o ? pipe(o, upgradeBoundsVersion, upgradeComparisonKeys) : o),
  z.object(zQuestionCfgShape)
)

export type QuestionCfg = z.infer<typeof zQuestionCfg>

/** usually we rely on default in zod, however for some things we want to keep things as they are and have a default for new */
export const questionCfgForNew = zQuestionCfg.parse({
  chart: {relative: true, showTails: true},
  aggVersion: '2',
  boundsVersion: '2',
  hideSubTitles: false
} satisfies Partial<QuestionCfg>)

export const zQuestionRow = z.object({
  id: z.string(),
  workspace_id: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string(),
  cfg: zQuestionCfg,
  deleted: z.boolean().default(false)
})
export type QuestionRow = z.infer<typeof zQuestionRow>

export const questionTypes = ['question', 'subquestion'] as const
export type QuestionType = (typeof questionTypes)[number]
export const questionTypeMd: Record<QuestionType, {label: string}> = {
  question: {label: 'Question'},
  subquestion: {label: 'Subquestion'}
}

export type QuestionForecastType = keyof typeof questionForecastMd
export type QuestionBounds = QuestionCfg['defaultBounds']
export type QuestionHardBounds = QuestionCfg['hardBounds']

export const questionResultQuestionProps = [
  'forecastType',
  'chart',
  'unitLabel',
  'unitLabelLong',
  'defaultBounds',
  'hardBounds',
  'numberFormat',
  'colors',
  'resolutions',
  'title',
  'disableQuestionForecast',
  'amountType',
  'subquestionOrder',
  'resolutionValues',
  'aggVersion',
  'subTitle',
  'hideSubTitles',
  'comparisons'
] as const

export const getResultsQuestion = (question: QuestionCfg & {id: string}) =>
  pick(question, ['id', ...questionResultQuestionProps])

export type QuestionResultsQuestion = ReturnType<typeof getResultsQuestion>

const dueStatus = ['question-review', 'conditional-generation', 'forecasting'] as const
export const isDueStatus = (s?: string): s is (typeof dueStatus)[number] => s != null && dueStatus.includes(s as any)

export const dueDate = (cfg: Pick<QuestionCfg, 'due' | 'status'>) =>
  isDueStatus(cfg.status) ? cfg.due[cfg.status].date : undefined
